/**
 * FCD Event Registration — LGND Baseball Launch Event
 * ----------------------------------------------------
 * Per-attendee glove + VIP model; optional guest tickets (separate product):
 *   - Step 1: attendee ticket count (deducts attendee inventory only).
 *   - With a glove: attendee ticket + up to 2 guests use $0 products (any age).
 *   - Under freeTicketUnderAge (default 10): attendee ticket is $0 even without a glove.
 *   - Guest 1 still required if under minAgeWithoutGuest.
 *
 * Owner: Forest City Digital
 */

(function () {
  'use strict';

  const SECTION_CLASS = 'fcd-event';
  const sections = document.querySelectorAll('.' + SECTION_CLASS);
  if (!sections.length) return;

  sections.forEach((section) => initSection(section));

  function initSection(section) {
    const sectionId = section.dataset.sectionId;
    const configEl = document.getElementById('fcd-event-config-' + sectionId);
    if (!configEl) {
      console.warn('FCD Event Registration: config block not found');
      return;
    }

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (err) {
      console.error('FCD Event Registration: failed to parse config', err);
      return;
    }

    const form = section.querySelector('#fcd-event-form');
    if (!form) return;

    const elements = {
      qtyInput: form.querySelector('#fcd-event-ticket-qty'),
      qtyButtons: form.querySelectorAll('.fcd-event__qty-btn'),
      attendeesContainer: form.querySelector('#fcd-event-attendees-container'),
      guestsSection: form.querySelector('#fcd-event-guests-section'),
      guestsContainer: form.querySelector('#fcd-event-guests-container'),
      guestsDesc: form.querySelector('#fcd-event-guests-desc'),
      guestsHeadingNote: form.querySelector('#fcd-event-guests-heading-note'),
      addGuestBtn: form.querySelector('#fcd-event-add-guest'),
      gloveModules: form.querySelector('#fcd-event-glove-modules'),
      gloveEmpty: form.querySelector('[data-glove-empty]'),
      summary: form.querySelector('#fcd-event-summary'),
      totalEl: form.querySelector('#fcd-event-total'),
      submitBtn: form.querySelector('#fcd-event-submit'),
      submitLabel: form.querySelector('.fcd-event__cta-label'),
      submitLoading: form.querySelector('.fcd-event__cta-loading'),
      errorEl: form.querySelector('#fcd-event-error')
    };

    const state = { quantity: 1, guestSlots: 1 };

    // ------ Glove data ------
    let gloveData = { products: [] };
    const gloveDataEl = document.getElementById('fcd-glove-data-' + sectionId);
    if (gloveDataEl) {
      try { gloveData = JSON.parse(gloveDataEl.textContent); }
      catch (err) { console.error('[FCD Event] Failed to parse glove variant data', err); }
    }
    const lowStockThreshold = parseInt(config.lowStockThreshold, 10) || 5;
    const gloveDefaultChecked = section.dataset.gloveDefault === 'true';
    const vipDefaultChecked = section.dataset.vipDefault === 'true';
    const vipEnabled = !!config.vipVariantId && config.vipAvailable;
    const guestsPerAttendee = parseInt(config.guestsPerAttendee, 10) || 2;
    const minAgeWithoutGuest = parseInt(config.minAgeWithoutGuest, 10) || 13;
    const freeTicketUnderAge = parseInt(config.freeTicketUnderAge, 10) || 10;
    const guestEnabled = !!config.guestVariantId && config.guestAvailable;
    const freeTicketEnabled = !!config.freeTicketVariantId && config.freeTicketAvailable;
    const freeGuestEnabled = !!config.freeGuestVariantId && config.freeGuestAvailable;

    function maxGuestSlots() {
      return state.quantity * guestsPerAttendee;
    }

    function isUnderFreeAge(age) {
      return age !== null && Number.isFinite(age) && age < minAgeWithoutGuest;
    }

    function isUnderChildFreeTicketAge(age) {
      return age !== null && Number.isFinite(age) && age < freeTicketUnderAge;
    }

    /** Free $0 attendee ticket: glove (any age) OR under freeTicketUnderAge (no glove required). */
    function qualifiesForFreeTicket(idx) {
      if (!freeTicketEnabled) return false;
      if (attendeeGloveOn(idx)) return true;
      const ages = getAttendeeAges();
      return isUnderChildFreeTicketAge(ages[idx]);
    }

    /** Free $0 guests only when that attendee buys a glove. */
    function qualifiesForFreeGuests(idx) {
      if (!freeGuestEnabled) return false;
      return attendeeGloveOn(idx);
    }

    function countFreeEligibleAttendees() {
      let count = 0;
      for (let i = 0; i < state.quantity; i++) {
        if (qualifiesForFreeTicket(i)) count += 1;
      }
      return count;
    }

    function freeGuestAllowance() {
      let count = 0;
      for (let i = 0; i < state.quantity; i++) {
        if (qualifiesForFreeGuests(i)) count += 1;
      }
      return count * guestsPerAttendee;
    }

    function getGloveProduct(productId) {
      if (!productId) return null;
      return gloveData.products.find((p) => String(p.id) === String(productId)) || null;
    }
    function getOptionIndices(product) {
      if (!product || !product.optionNames) return { handIdx: -1, sizeIdx: -1 };
      const lower = product.optionNames.map((n) => String(n).toLowerCase());
      const handIdx = lower.findIndex((n) => n.includes('hand') || n.includes('throw'));
      const sizeIdx = lower.findIndex((n) => n.includes('size'));
      return { handIdx, sizeIdx };
    }
    function getHandsForProduct(product) {
      const { handIdx } = getOptionIndices(product);
      if (handIdx === -1) return [];
      const seen = new Map();
      product.variants.forEach((v) => {
        const h = v.options[handIdx];
        if (!seen.has(h)) seen.set(h, { value: h, available: false });
        if (v.available) seen.get(h).available = true;
      });
      return Array.from(seen.values());
    }
    function getSizesForProductHand(product, handValue) {
      const { handIdx, sizeIdx } = getOptionIndices(product);
      if (handIdx === -1 || sizeIdx === -1 || !handValue) return [];
      return product.variants
        .filter((v) => v.options[handIdx] === handValue)
        .map((v) => ({ value: v.options[sizeIdx], available: v.available, inventory: v.inventoryQuantity, variantId: v.id, price: v.price }));
    }
    function findVariant(product, handValue, sizeValue) {
      const { handIdx, sizeIdx } = getOptionIndices(product);
      if (handIdx === -1 || sizeIdx === -1) return null;
      return product.variants.find((v) => v.options[handIdx] === handValue && v.options[sizeIdx] === sizeValue) || null;
    }

    function formatMoney(cents) {
      const neg = cents < 0;
      return (neg ? '-$' : '$') + (Math.abs(cents) / 100).toFixed(2);
    }
    function escape(str) {
      return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    // ------ Quantity ------
    function setQuantity(newQty) {
      const min = parseInt(elements.qtyInput.min, 10) || 1;
      const max = parseInt(elements.qtyInput.max, 10) || config.maxPerOrder;
      newQty = Math.max(min, Math.min(max, newQty));
      state.quantity = newQty;
      elements.qtyInput.value = newQty;
      state.guestSlots = Math.min(Math.max(state.guestSlots, 1), maxGuestSlots());
      renderAttendees();
      renderGuests();
      renderGloveModules();
      updateSummary();
    }
    elements.qtyButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        const current = parseInt(elements.qtyInput.value, 10) || 1;
        setQuantity(action === 'increment' ? current + 1 : current - 1);
      });
    });
    elements.qtyInput.addEventListener('input', () => setQuantity(parseInt(elements.qtyInput.value, 10) || 1));

    // ------ Attendee blocks (Step 2) — compact, checkbox only ------
    // Holds preserved glove/vip selections across re-renders, keyed by attendee index.
    let gloveSelections = {}; // idx -> { productId, hand, size, vip }

    function getGloveOptionValue(mod, fieldKey) {
      return mod.querySelector(`input[data-field-key="${fieldKey}"]:checked`)?.value || '';
    }

    function captureGloveSelections() {
      const out = {};
      if (!elements.gloveModules) return out;
      elements.gloveModules.querySelectorAll('[data-glove-module]').forEach((mod) => {
        const idx = mod.dataset.attendeeIndex;
        out[idx] = {
          productId: mod.querySelector('input[data-glove-color]:checked')?.value || '',
          hand: getGloveOptionValue(mod, 'glove_hand'),
          size: getGloveOptionValue(mod, 'glove_size'),
          vip: mod.querySelector('[data-field-key="add_vip"]')?.checked || false
        };
      });
      return out;
    }

    function renderAttendees() {
      const container = elements.attendeesContainer;
      if (!container) return;
      const currentBlocks = container.querySelectorAll('.fcd-event__attendee');
      const existingData = [];
      currentBlocks.forEach((block) => {
        const data = {};
        block.querySelectorAll('input, select, textarea').forEach((input) => {
          const key = input.dataset.fieldKey;
          if (!key) return;
          data[key] = input.type === 'checkbox' ? input.checked : input.value;
        });
        existingData.push(data);
      });
      container.innerHTML = '';
      for (let i = 0; i < state.quantity; i++) {
        container.appendChild(buildAttendeeBlock(i, existingData[i] || {}));
      }
    }

    function buildAttendeeBlock(index, data) {
      const wrap = document.createElement('div');
      wrap.className = 'fcd-event__attendee';
      wrap.dataset.attendeeIndex = index;
      const gloveChecked = data.add_glove !== undefined ? data.add_glove : gloveDefaultChecked;

      wrap.innerHTML = `
        <h3 class="fcd-event__attendee-title">Attendee ${index + 1}</h3>

        <h4 class="fcd-event__subheading">Attendee info</h4>
        <p class="fcd-event__info-note">
          <span class="fcd-event__info-note-icon" aria-hidden="true">ⓘ</span>
          If the attendee is a minor, enter the legal guardian's contact information so we can reach you about the event.
        </p>
        <div class="fcd-event__field-grid">
          <div class="fcd-event__field">
            <label>Attendee's name <span class="fcd-event__req">*</span></label>
            <input type="text" data-field-key="kid_name" required value="${escape(data.kid_name || '')}">
          </div>
          <div class="fcd-event__field">
            <label>Attendee's age <span class="fcd-event__req">*</span></label>
            <input type="number" min="3" max="99" data-field-key="kid_age" required value="${escape(data.kid_age || '')}">
          </div>
          <div class="fcd-event__field">
            <label>Are they a player? <span class="fcd-event__req">*</span></label>
            <select data-field-key="is_player" required>
              <option value="" ${!data.is_player ? 'selected' : ''} disabled>Select an option</option>
              <option value="Yes" ${data.is_player === 'Yes' ? 'selected' : ''}>Yes — playing</option>
              <option value="No" ${data.is_player === 'No' ? 'selected' : ''}>No — attending only</option>
            </select>
          </div>
          <div class="fcd-event__field">
            <label>Attendee's email <span class="fcd-event__req">*</span></label>
            <input type="email" data-field-key="kid_email" required autocomplete="email" inputmode="email" value="${escape(data.kid_email || '')}">
          </div>
          <div class="fcd-event__field">
            <label>Attendee's phone <span class="fcd-event__req">*</span></label>
            <input type="tel" data-field-key="kid_phone" required autocomplete="tel" inputmode="tel" value="${escape(data.kid_phone || '')}">
          </div>
        </div>

        <div class="fcd-event__checkbox-stack">
          <label class="fcd-event__inline-check">
            <input type="checkbox" data-field-key="waiver_photo" ${data.waiver_photo ? 'checked' : ''}>
            <span>I grant LGND Baseball permission to use photos/video featuring this attendee in marketing materials.</span>
          </label>
        </div>

      `;
      //Buy a glove removed from request
      return wrap;
    }
    //Buy a glove div code
    /*
    <div class="fcd-event__attendee-glove">
      <label class="fcd-event__toggle">
        <input type="checkbox" data-field-key="add_glove" ${gloveChecked ? 'checked' : ''}>
        <span class="fcd-event__toggle-label">Add a LGND glove for this attendee — $249</span>
      </label>
    </div>
    */

    // ------ Guest blocks (optional, separate ticket product) ------
    function captureGuestData() {
      const out = [];
      if (!elements.guestsContainer) return out;
      elements.guestsContainer.querySelectorAll('.fcd-event__guest-block').forEach((block) => {
        const data = {};
        block.querySelectorAll('input[data-field-key]').forEach((input) => {
          data[input.dataset.fieldKey] = input.value;
        });
        out.push(data);
      });
      return out;
    }

    function isGuestBlockActive(block) {
      return Array.from(block.querySelectorAll('input[data-field-key]')).some((input) => input.value.trim());
    }

    function isFirstGuestBlock(block) {
      return parseInt(block.dataset.guestIndex, 10) === 0;
    }

    function getAttendeeAges() {
      const ages = [];
      if (!elements.attendeesContainer) return ages;
      elements.attendeesContainer.querySelectorAll('.fcd-event__attendee').forEach((block) => {
        const raw = block.querySelector('[data-field-key="kid_age"]')?.value?.trim();
        if (raw === '') {
          ages.push(null);
          return;
        }
        const age = parseInt(raw, 10);
        ages.push(Number.isFinite(age) ? age : null);
      });
      return ages;
    }

    /** Guest 1 required when any attendee age is missing or under the configured minimum. */
    function isGuest1Required() {
      const ages = getAttendeeAges();
      if (!ages.length) return true;
      return ages.some((age) => age === null || age < minAgeWithoutGuest);
    }

    function shouldIncludeGuestBlock(block) {
      if (!isFirstGuestBlock(block)) return isGuestBlockActive(block);
      return isGuest1Required() || isGuestBlockActive(block);
    }

    function countGuestTickets() {
      if (!guestEnabled || !elements.guestsContainer) return 0;
      let count = 0;
      elements.guestsContainer.querySelectorAll('.fcd-event__guest-block').forEach((block) => {
        if (shouldIncludeGuestBlock(block)) count += 1;
      });
      return count;
    }

    function renderGuests(existingData) {
      if (!guestEnabled || !elements.guestsContainer) return;
      const data = existingData || captureGuestData();
      elements.guestsContainer.innerHTML = '';
      for (let i = 0; i < state.guestSlots; i++) {
        elements.guestsContainer.appendChild(buildGuestBlock(i, data[i] || {}));
      }
      syncGuest1Requirement();
      updateGuestsDesc();
      updateAddGuestButton();
    }

    function buildGuestBlock(index, data) {
      const wrap = document.createElement('div');
      wrap.className = 'fcd-event__guest-block';
      wrap.dataset.guestIndex = index;
      const showRemove = index > 0;
      const guest1Required = index === 0 && isGuest1Required();

      wrap.innerHTML = `
        <div class="fcd-event__guest-header">
          <span class="fcd-event__guest-title">Guest ${index + 1}</span>
          ${showRemove ? '<button type="button" class="fcd-event__remove-guest" data-action="remove-guest" aria-label="Remove guest">×</button>' : ''}
        </div>
        <p class="fcd-event__info-note fcd-event__info-note--compact" data-guest-notice ${index === 0 ? 'hidden' : ''}>
          ${index === 0 ? '' : `<span class="fcd-event__info-note-icon" aria-hidden="true">ⓘ</span> Leave blank to skip this guest. A guest ticket is only added when you complete the fields below.`}
        </p>
        <div class="fcd-event__field-grid">
          <div class="fcd-event__field">
            <label>Guest name<span class="fcd-event__req" data-guest-req hidden> *</span></label>
            <input type="text" data-field-key="guest_name" value="${escape(data.guest_name || '')}">
          </div>
          <div class="fcd-event__field">
            <label>Guest email<span class="fcd-event__req" data-guest-req hidden> *</span></label>
            <input type="email" data-field-key="guest_email" autocomplete="email" inputmode="email" value="${escape(data.guest_email || '')}">
          </div>
          <div class="fcd-event__field">
            <label>Guest phone<span class="fcd-event__req" data-guest-req hidden> *</span></label>
            <input type="tel" data-field-key="guest_phone" autocomplete="tel" inputmode="tel" value="${escape(data.guest_phone || '')}">
          </div>
        </div>
      `;
      if (index === 0) applyGuest1RequiredState(wrap, guest1Required);
      return wrap;
    }

    function applyGuest1RequiredState(block, required) {
      if (!block || !isFirstGuestBlock(block)) return;
      const title = block.querySelector('.fcd-event__guest-title');
      const notice = block.querySelector('[data-guest-notice]');
      const reqMarkers = block.querySelectorAll('[data-guest-req]');
      const inputs = block.querySelectorAll('input[data-field-key]');

      if (title) title.textContent = required ? 'Guest 1 (required)' : 'Guest 1 (optional)';
      reqMarkers.forEach((el) => { el.hidden = !required; });
      inputs.forEach((input) => {
        if (required) input.setAttribute('required', '');
        else input.removeAttribute('required');
      });

      if (notice) {
        notice.hidden = false;
        notice.innerHTML = required
          ? `<span class="fcd-event__info-note-icon" aria-hidden="true">ⓘ</span> At least one guest is required when any attendee is under ${minAgeWithoutGuest}.`
          : `<span class="fcd-event__info-note-icon" aria-hidden="true">ⓘ</span> All attendees are ${minAgeWithoutGuest}+ — Guest 1 is optional. Leave blank to skip, or complete the fields to add a guest ticket.`;
      }
    }

    function syncGuest1Requirement() {
      if (!elements.guestsContainer) return;
      const block = elements.guestsContainer.querySelector('.fcd-event__guest-block[data-guest-index="0"]');
      if (block) applyGuest1RequiredState(block, isGuest1Required());
      if (elements.guestsHeadingNote) {
        elements.guestsHeadingNote.textContent = isGuest1Required() ? '(1 required)' : '(optional)';
      }
      updateGuestsDesc();
    }

    function updateGuestsDesc() {
      if (!elements.guestsDesc) return;
      const max = maxGuestSlots();
      const freeGuests = freeGuestAllowance();
      let text;
      if (isGuest1Required()) {
        text = `Guest 1 is required while any attendee is under ${minAgeWithoutGuest}. Each attendee may bring up to ${guestsPerAttendee} guests — up to ${max} guest ticket${max === 1 ? '' : 's'} total.`;
      } else {
        text = `All attendees are ${minAgeWithoutGuest}+, so Guest 1 is optional. You may still add up to ${max} guest ticket${max === 1 ? '' : 's'} (${guestsPerAttendee} per attendee).`;
      }
      if (freeTicketEnabled) {
        //text += ` Attendees under ${freeTicketUnderAge} get a free event ticket (glove not required). Each attendee who adds a LGND glove gets a free event ticket and up to ${guestsPerAttendee} free guest ticket${guestsPerAttendee === 1 ? '' : 's'}.`;
        text += ` Attendees under ${freeTicketUnderAge} get a free event ticket.`;
        if (freeGuests > 0) {
          text += ` (${freeGuests} free guest slot${freeGuests === 1 ? '' : 's'} from glove purchases on this order.)`;
        }
      }
      elements.guestsDesc.textContent = text;
    }

    function updateAddGuestButton() {
      if (!elements.addGuestBtn) return;
      const max = maxGuestSlots();
      const canAdd = state.guestSlots < max;
      elements.addGuestBtn.hidden = !canAdd;
      if (canAdd) {
        elements.addGuestBtn.textContent = `+ Add another guest (${state.guestSlots} of ${max})`;
      }
    }

    function addGuestSlot() {
      if (state.guestSlots >= maxGuestSlots()) return;
      state.guestSlots += 1;
      renderGuests(captureGuestData());
      updateSummary();
    }

    function removeGuestSlot(index) {
      if (state.guestSlots <= 1) return;
      const data = captureGuestData();
      data.splice(index, 1);
      state.guestSlots -= 1;
      renderGuests(data);
      updateSummary();
    }

    // ------ Attendee name helper ------
    function attendeeName(idx) {
      const block = elements.attendeesContainer.querySelector(`.fcd-event__attendee[data-attendee-index="${idx}"]`);
      const name = block?.querySelector('[data-field-key="kid_name"]')?.value?.trim();
      return name || `Attendee ${idx + 1}`;
    }
    function attendeeGloveOn(idx) {
      const block = elements.attendeesContainer.querySelector(`.fcd-event__attendee[data-attendee-index="${idx}"]`);
      return !!block?.querySelector('[data-field-key="add_glove"]')?.checked;
    }

    // ------ Glove modules (Step 3) — one per checked attendee ------
    function renderGloveModules() {
      if (!elements.gloveModules) return;
      // preserve current selections before wiping
      gloveSelections = Object.assign({}, gloveSelections, captureGloveSelections());

      elements.gloveModules.innerHTML = '';
      let any = false;
      for (let i = 0; i < state.quantity; i++) {
        if (!attendeeGloveOn(i)) continue;
        any = true;
        elements.gloveModules.appendChild(buildGloveModule(i, gloveSelections[i] || {}));
      }
      if (elements.gloveEmpty) elements.gloveEmpty.hidden = any;
    }

    function getDefaultGloveProductId() {
      const firstAvailable = gloveData.products.find((p) => p.available);
      return firstAvailable ? String(firstAvailable.id) : '';
    }

    function buildGloveModule(index, sel) {
      const mod = document.createElement('div');
      mod.className = 'fcd-event__glove-module';
      mod.setAttribute('data-glove-module', '');
      mod.dataset.attendeeIndex = index;
      const vipChecked = sel.vip !== undefined ? sel.vip : vipDefaultChecked;
      const selectedProductId = sel.productId || getDefaultGloveProductId();

      mod.innerHTML = `
        <h3 class="fcd-event__glove-module-title">${escape(possessive(attendeeName(index)))} glove</h3>
        <div class="fcd-event__glove-grid" data-glove-grid>
          ${buildColorGridHtml(index, selectedProductId)}
        </div>
        <div class="fcd-event__variant-pickers">
          <fieldset
            class="variant-option variant-option--buttons fcd-event__variant-option"
            data-glove-hand-option
            data-preserved-value="${escape(sel.hand || '')}"
          >
            <legend>Hand <span class="fcd-event__req">*</span></legend>
            <p class="fcd-event__variant-hint" data-hand-hint>Pick a glove design first</p>
            <div class="fcd-event__variant-buttons" data-hand-buttons></div>
          </fieldset>
          <fieldset
            class="variant-option variant-option--buttons fcd-event__variant-option"
            data-glove-size-option
            data-preserved-value="${escape(sel.size || '')}"
          >
            <legend>Glove Size <span class="fcd-event__req">*</span></legend>
            <p class="fcd-event__variant-hint" data-size-hint>Pick a hand first</p>
            <div class="fcd-event__variant-buttons" data-size-buttons></div>
          </fieldset>
        </div>
        ${vipEnabled ? `
        <div class="fcd-event__attendee-vip">
          <h4 class="fcd-event__subheading">Make it legendary</h4>
          <p class="fcd-event__vip-requires"><span aria-hidden="true">🧤</span> Included with this attendee's glove</p>
          <ul class="fcd-event__vip-perks">
            <li>📸 Photo with Mookie Betts</li>
            <li>⚾ Exclusive 1-Hour On-Field Access with Mookie</li>
            <li>🎁 Exclusive VIP welcome gift</li>
          </ul>
          <label class="fcd-event__toggle">
            <input type="checkbox" data-field-key="add_vip" ${vipChecked ? 'checked' : ''}>
            <span class="fcd-event__toggle-label">Yes — upgrade ${escape(attendeeName(index))} to VIP (+$${(config.vipPrice/100).toFixed(0)})</span>
          </label>
        </div>` : ''}
      `;
      populateHandSelector(mod);
      return mod;
    }

    function strikethroughSvg() {
      return `<svg width="100%" height="100%" viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice" aria-hidden="true"><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"></line></svg>`;
    }

    function buildVariantPill({ name, value, available, checked, fieldKey, variantId, title }) {
      const tip = title ? ` title="${escape(title)}"` : '';
      return `
        <label class="variant-option__button-label"${tip}>
          <input
            type="radio"
            name="${escape(name)}"
            value="${escape(value)}"
            data-field-key="${escape(fieldKey)}"
            data-option-available="${available ? 'true' : 'false'}"
            ${variantId != null ? `data-variant-id="${escape(String(variantId))}"` : ''}
            ${available ? '' : 'disabled aria-disabled="true"'}
            ${checked ? 'checked' : ''}
          >
          <span class="variant-option__button-label__text">${escape(value)}</span>
          ${available ? '' : strikethroughSvg()}
        </label>
      `;
    }

    function buildColorGridHtml(attendeeIndex, selectedProductId) {
      return gloveData.products.map((p) => {
        const soldOut = !p.available;
        const checked = String(p.id) === String(selectedProductId) ? 'checked' : '';
        const img = p.image ? `<img src="${escape(p.image)}" loading="lazy" alt="${escape(p.title)}">` : '';
        return `
          <label class="fcd-event__glove-card ${soldOut ? 'fcd-event__glove-card--soldout' : ''}"
                 data-glove-product-id="${escape(String(p.id))}"
                 data-glove-price="${escape(String(p.priceMin))}"
                 data-glove-name="${escape(p.title)}">
            <input type="radio" name="glove_color_${attendeeIndex}" value="${escape(String(p.id))}"
                   data-glove-color ${soldOut ? 'disabled' : ''} ${checked}>
            <div class="fcd-event__glove-card-media">${img}</div>
            <div class="fcd-event__glove-card-body">
              <h3 class="fcd-event__glove-card-title">${escape(p.title)}</h3>
              ${soldOut ? '<p class="fcd-event__glove-card-soldout">SOLD OUT</p>' : ''}
            </div>
          </label>
        `;
      }).join('');
    }

    function possessive(name) {
      return /s$/i.test(name) ? name + "'" : name + "'s";
    }

    // ------ Cascading hand → size pills (theme variant-option markup) ------
    function getModuleProductId(mod) {
      return mod.querySelector('input[data-glove-color]:checked')?.value || '';
    }

    function setVariantHint(fieldset, text) {
      const hint = fieldset?.querySelector('.fcd-event__variant-hint');
      if (!hint) return;
      if (text) {
        hint.hidden = false;
        hint.textContent = text;
      } else {
        hint.hidden = true;
        hint.textContent = '';
      }
    }

    function populateHandSelector(mod) {
      const handField = mod.querySelector('[data-glove-hand-option]');
      const sizeField = mod.querySelector('[data-glove-size-option]');
      const handButtons = handField?.querySelector('[data-hand-buttons]');
      const sizeButtons = sizeField?.querySelector('[data-size-buttons]');
      if (!handField || !handButtons || !sizeField || !sizeButtons) return;

      const attendeeIndex = mod.dataset.attendeeIndex;
      const product = getGloveProduct(getModuleProductId(mod));
      if (!product) {
        handButtons.innerHTML = '';
        sizeButtons.innerHTML = '';
        setVariantHint(handField, 'Pick a glove design first');
        setVariantHint(sizeField, 'Pick a glove design first');
        return;
      }

      const hands = getHandsForProduct(product);
      let prev = getGloveOptionValue(mod, 'glove_hand') || handField.dataset.preservedValue || '';
      if (!prev || !hands.find((h) => h.value === prev && h.available)) {
        prev = hands.find((h) => h.available)?.value || '';
      }

      if (hands.length === 0) {
        handButtons.innerHTML = buildVariantPill({
          name: `glove_hand_${attendeeIndex}`,
          value: 'N/A',
          available: true,
          checked: true,
          fieldKey: 'glove_hand'
        });
        setVariantHint(handField, null);
      } else {
        handButtons.innerHTML = hands.map((h) => buildVariantPill({
          name: `glove_hand_${attendeeIndex}`,
          value: h.value,
          available: h.available,
          checked: h.available && h.value === prev,
          fieldKey: 'glove_hand',
          title: h.available ? h.value : `${h.value} (sold out)`
        })).join('');
        setVariantHint(handField, null);
      }
      handField.dataset.preservedValue = '';
      populateSizeSelector(mod);
    }

    function populateSizeSelector(mod) {
      const handField = mod.querySelector('[data-glove-hand-option]');
      const sizeField = mod.querySelector('[data-glove-size-option]');
      const sizeButtons = sizeField?.querySelector('[data-size-buttons]');
      if (!handField || !sizeField || !sizeButtons) return;

      const attendeeIndex = mod.dataset.attendeeIndex;
      const product = getGloveProduct(getModuleProductId(mod));
      if (!product) return;

      const { sizeIdx } = getOptionIndices(product);
      if (sizeIdx === -1) {
        sizeButtons.innerHTML = buildVariantPill({
          name: `glove_size_${attendeeIndex}`,
          value: 'N/A',
          available: true,
          checked: true,
          fieldKey: 'glove_size'
        });
        setVariantHint(sizeField, null);
        return;
      }

      const handValue = getGloveOptionValue(mod, 'glove_hand');
      if (!handValue) {
        sizeButtons.innerHTML = '';
        setVariantHint(sizeField, 'Pick a hand first');
        return;
      }

      const sizes = getSizesForProductHand(product, handValue);
      let prev = getGloveOptionValue(mod, 'glove_size') || sizeField.dataset.preservedValue || '';
      if (!prev || !sizes.find((s) => s.value === prev && s.available)) {
        prev = sizes.find((s) => s.available)?.value || '';
      }
      sizeButtons.innerHTML = sizes.map((s) => {
        let title = s.value;
        if (!s.available) title = `${s.value} (sold out)`;
        else if (s.inventory !== null && s.inventory > 0 && s.inventory <= lowStockThreshold) {
          title = `${s.value} — only ${s.inventory} left`;
        }
        return buildVariantPill({
          name: `glove_size_${attendeeIndex}`,
          value: s.value,
          available: s.available,
          checked: s.available && s.value === prev,
          fieldKey: 'glove_size',
          variantId: s.variantId,
          title
        });
      }).join('');
      setVariantHint(sizeField, null);
      sizeField.dataset.preservedValue = '';
    }

    // ------ Events ------
    // Attendee container: glove checkbox toggles modules; name edits refresh module titles.
    elements.attendeesContainer.addEventListener('change', (e) => {
      const t = e.target;
      if (t && t.dataset.fieldKey === 'add_glove') {
        renderGloveModules();
        updateGuestsDesc();
        updateSummary();
      }
    });
    elements.attendeesContainer.addEventListener('input', (e) => {
      const t = e.target;
      if (t && t.dataset.fieldKey === 'kid_age') {
        syncGuest1Requirement();
        updateSummary();
        return;
      }
      if (t && t.dataset.fieldKey === 'kid_name') {
        // live-update the matching module title
        const idx = t.closest('.fcd-event__attendee')?.dataset.attendeeIndex;
        if (idx != null && elements.gloveModules) {
          const mod = elements.gloveModules.querySelector(`[data-glove-module][data-attendee-index="${idx}"]`);
          const title = mod?.querySelector('.fcd-event__glove-module-title');
          if (title) title.textContent = `${possessive(attendeeName(parseInt(idx, 10)))} glove`;
          const vipLabel = mod?.querySelector('.fcd-event__attendee-vip .fcd-event__toggle-label');
          if (vipLabel) vipLabel.textContent = `Yes — upgrade ${attendeeName(parseInt(idx, 10))} to VIP (+$${(config.vipPrice/100).toFixed(0)})`;
        }
        updateSummary();
      }
    });

    if (elements.guestsContainer) {
      elements.guestsContainer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action="remove-guest"]');
        if (!btn) return;
        const block = btn.closest('.fcd-event__guest-block');
        if (!block) return;
        removeGuestSlot(parseInt(block.dataset.guestIndex, 10));
      });
      elements.guestsContainer.addEventListener('input', () => updateSummary());
    }
    if (elements.addGuestBtn) {
      elements.addGuestBtn.addEventListener('click', addGuestSlot);
    }

    // Glove modules: color / hand / size / vip changes
    if (elements.gloveModules) {
      elements.gloveModules.addEventListener('change', (e) => {
        const t = e.target;
        const mod = t.closest('[data-glove-module]');
        if (!mod) return;
        if (t.dataset.gloveColor !== undefined) { populateHandSelector(mod); updateSummary(); return; }
        if (t.dataset.fieldKey === 'glove_hand') { populateSizeSelector(mod); updateSummary(); return; }
        if (t.dataset.fieldKey === 'glove_size' || t.dataset.fieldKey === 'add_vip') { updateSummary(); return; }
      });
    }

    // ------ Summary ------
    function updateSummary() {
      const lines = [];
      let total = 0;
      let freeAttendeeCount = 0;
      let paidAttendeeCount = 0;
      for (let i = 0; i < state.quantity; i++) {
        if (qualifiesForFreeTicket(i)) freeAttendeeCount += 1;
        else paidAttendeeCount += 1;
      }

      if (freeAttendeeCount > 0) {
        const freeAmt = (config.freeTicketPrice || 0) * freeAttendeeCount;
        lines.push({
          label: `${freeAttendeeCount} × Attendee Ticket (free)`,
          amount: freeAmt,
          discount: freeAmt === 0
        });
        total += freeAmt;
      }
      if (paidAttendeeCount > 0) {
        const paidAmt = config.ticketPrice * paidAttendeeCount;
        lines.push({ label: `${paidAttendeeCount} × Attendee Ticket`, amount: paidAmt });
        total += paidAmt;
      }

      const guestCount = countGuestTickets();
      if (guestCount > 0 && guestEnabled) {
        const freeGuestSlots = freeGuestEnabled ? Math.min(guestCount, freeGuestAllowance()) : 0;
        const paidGuestCount = guestCount - freeGuestSlots;
        if (freeGuestSlots > 0) {
          const freeGuestAmt = (config.freeGuestPrice || 0) * freeGuestSlots;
          lines.push({
            label: `${freeGuestSlots} × Guest Ticket (glove — free)`,
            amount: freeGuestAmt,
            discount: freeGuestAmt === 0
          });
          total += freeGuestAmt;
        }
        if (paidGuestCount > 0) {
          const paidGuestAmt = config.guestPrice * paidGuestCount;
          lines.push({ label: `${paidGuestCount} × Guest Ticket`, amount: paidGuestAmt });
          total += paidGuestAmt;
        }
      }

      for (let i = 0; i < state.quantity; i++) {
        if (!attendeeGloveOn(i)) continue;
        const mod = elements.gloveModules.querySelector(`[data-glove-module][data-attendee-index="${i}"]`);
        if (!mod) continue;
        const productId = getModuleProductId(mod);
        const name = attendeeName(i);
        if (productId) {
          const product = getGloveProduct(productId);
          const price = product ? product.priceMin : 0;
          lines.push({ label: `Glove — ${name}: ${product ? product.title : ''}`, amount: price });
          total += price;
          const vipOn = mod.querySelector('[data-field-key="add_vip"]')?.checked;
          if (vipOn && vipEnabled) {
            lines.push({ label: `VIP Upgrade — ${name}`, amount: config.vipPrice });
            total += config.vipPrice;
          }
        } else {
          lines.push({ label: `Glove — ${name}: please pick a color ↑`, amount: 0, warning: true });
        }
      }

      if (total < 0) total = 0;

      elements.summary.innerHTML = lines.map((line) => `
        <div class="fcd-event__summary-line${line.warning ? ' fcd-event__summary-line--warn' : ''}${line.discount ? ' fcd-event__summary-line--discount' : ''}">
          <span>${escape(line.label)}</span>
          <span>${formatMoney(line.amount)}</span>
        </div>
      `).join('');
      elements.totalEl.textContent = formatMoney(total);
    }

    // ------ Validation ------
    function isValidEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim()); }
    function isValidPhone(v) { const d = String(v).replace(/[^\d]/g, ''); return d.length >= 7 && d.length <= 15; }
    function flagFieldError(input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.style.outline = '2px solid var(--fcd-error, #b3261e)';
      input.style.outlineOffset = '2px';
      setTimeout(() => { input.style.outline = ''; input.style.outlineOffset = ''; }, 3000);
    }
    function describeField(input) {
      const guestBlock = input.closest('.fcd-event__guest-block');
      const fieldLabel = input.closest('.fcd-event__field, .fcd-event__inline-check')?.querySelector('label, span')?.textContent?.trim().replace(/[*\s]+$/, '') || 'this field';
      if (guestBlock) {
        const n = parseInt(guestBlock.dataset.guestIndex, 10) + 1;
        return `Guest ${n} → ${fieldLabel}`;
      }
      const attendeeBlock = input.closest('.fcd-event__attendee');
      if (!attendeeBlock) return fieldLabel;
      const n = parseInt(attendeeBlock.dataset.attendeeIndex, 10) + 1;
      return `Attendee ${n} → ${fieldLabel}`;
    }

    function validateRegistration() {
      const requiredInputs = form.querySelectorAll(
        '.fcd-event__attendee input[required], .fcd-event__attendee select[required], .fcd-event__attendee textarea[required]'
      );
      for (const input of requiredInputs) {
        if (input.offsetParent === null) continue;
        const isEmpty = input.type === 'checkbox' ? !input.checked : !input.value.trim();
        if (isEmpty) { flagFieldError(input); return { valid: false, field: input, reason: 'empty' }; }
      }
      const formatInputs = form.querySelectorAll('.fcd-event__attendee input[type="email"], .fcd-event__attendee input[type="tel"], .fcd-event__guest-block input[type="email"], .fcd-event__guest-block input[type="tel"]');
      for (const input of formatInputs) {
        if (input.offsetParent === null) continue;
        const value = input.value.trim();
        if (!value) continue;
        if (input.type === 'email' && !isValidEmail(value)) { flagFieldError(input); return { valid: false, field: input, reason: 'email_format' }; }
        if (input.type === 'tel' && !isValidPhone(value)) { flagFieldError(input); return { valid: false, field: input, reason: 'phone_format' }; }
      }

      if (elements.guestsContainer) {
        const guestBlocks = elements.guestsContainer.querySelectorAll('.fcd-event__guest-block');
        for (const block of guestBlocks) {
          if (!shouldIncludeGuestBlock(block)) continue;
          const name = block.querySelector('[data-field-key="guest_name"]');
          const email = block.querySelector('[data-field-key="guest_email"]');
          const phone = block.querySelector('[data-field-key="guest_phone"]');
          if (!name?.value.trim()) { flagFieldError(name); return { valid: false, field: name, reason: 'empty' }; }
          if (!email?.value.trim()) { flagFieldError(email); return { valid: false, field: email, reason: 'empty' }; }
          if (!phone?.value.trim()) { flagFieldError(phone); return { valid: false, field: phone, reason: 'empty' }; }
        }
      }

      // Per-attendee glove completeness
      const claims = new Map();
      for (let i = 0; i < state.quantity; i++) {
        if (!attendeeGloveOn(i)) continue;
        const mod = elements.gloveModules.querySelector(`[data-glove-module][data-attendee-index="${i}"]`);
        const name = attendeeName(i);
        const productId = mod ? getModuleProductId(mod) : '';
        if (!productId) return { valid: false, custom: `${possessive(name)} glove: please pick a color, or uncheck the glove option for this attendee.` };
        const hand = getGloveOptionValue(mod, 'glove_hand');
        const size = getGloveOptionValue(mod, 'glove_size');
        if (!hand) return { valid: false, custom: `${possessive(name)} glove: please choose a hand preference.` };
        if (!size) return { valid: false, custom: `${possessive(name)} glove: please choose a size.` };
        const product = getGloveProduct(productId);
        const variant = findVariant(product, hand, size);
        if (variant) {
          const existing = claims.get(variant.id) || { count: 0, variant, product };
          existing.count += 1;
          claims.set(variant.id, existing);
        }
      }
      for (const { count, variant, product } of claims.values()) {
        if (variant.inventoryQuantity !== null && variant.inventoryQuantity < count) {
          return { valid: false, custom: `Only ${variant.inventoryQuantity} of "${product.title} — ${variant.title}" remaining, but ${count} attendees selected it. Please adjust hand or size.` };
        }
      }

      const freeEligibleCount = countFreeEligibleAttendees();
      if (freeEligibleCount > 0 && !freeTicketEnabled) {
        return {
          valid: false,
          custom: `Free ($0) attendee tickets are required for glove purchases and attendees under ${freeTicketUnderAge}. Please contact support — product is not available.`
        };
      }
      if (freeGuestAllowance() > 0 && countGuestTickets() > 0 && !freeGuestEnabled) {
        return {
          valid: false,
          custom: `Attendees with a glove require free ($0) guest tickets. Please contact support — product is not available.`
        };
      }

      return { valid: true };
    }

    async function handleSubmit() {
      hideError();
      const v = validateRegistration();
      if (!v.valid) {
        if (v.custom) { showError(v.custom); return; }
        let msg;
        if (v.reason === 'email_format') msg = `Please enter a valid email address for: ${describeField(v.field)}`;
        else if (v.reason === 'phone_format') msg = `Please enter a valid phone number for: ${describeField(v.field)}`;
        else msg = `Please complete: ${describeField(v.field)}`;
        showError(msg);
        return;
      }
      setLoading(true);
      try {
        const items = buildCartItems();
        await addItemsToCart(items);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('FCD Event Registration: cart add failed', err);
        showError(err.message || 'Something went wrong adding to cart. Please try again or contact support.');
        setLoading(false);
      }
    }

    function buildCartItems() {
      const items = [];
      const gloveLineItems = [];
      let vipCount = 0;
      const freeAttendeeProps = {};
      const paidAttendeeProps = {};
      let freeAttendeeCount = 0;
      let paidAttendeeCount = 0;
      let freeAttendeeLabel = 0;
      let paidAttendeeLabel = 0;

      const blocks = elements.attendeesContainer.querySelectorAll('.fcd-event__attendee');
      blocks.forEach((block, idx) => {
        const n = idx + 1;
        const get = (key) => {
          const el = block.querySelector(`[data-field-key="${key}"]`);
          if (!el) return '';
          return el.type === 'checkbox' ? (el.checked ? 'Yes' : 'No') : el.value;
        };
        const name = get('kid_name');
        const ageRaw = get('kid_age');
        const useFreeTicket = qualifiesForFreeTicket(idx);
        const targetProps = useFreeTicket ? freeAttendeeProps : paidAttendeeProps;
        const labelN = useFreeTicket ? (++freeAttendeeLabel) : (++paidAttendeeLabel);
        if (useFreeTicket) freeAttendeeCount += 1;
        else paidAttendeeCount += 1;

        targetProps[`Attendee ${labelN} — Name`] = name;
        targetProps[`Attendee ${labelN} — Age`] = ageRaw;
        targetProps[`Attendee ${labelN} — Player?`] = get('is_player');
        targetProps[`Attendee ${labelN} — Email`] = get('kid_email');
        targetProps[`Attendee ${labelN} — Phone`] = get('kid_phone');
        targetProps[`Attendee ${labelN} — Photo Release`] = get('waiver_photo');
        targetProps[`Attendee ${labelN} — Form #`] = String(n);
        if (useFreeTicket) {
          const reason = attendeeGloveOn(idx)
            ? 'Glove purchase — free ticket'
            : `Under ${freeTicketUnderAge} — free ticket`;
          targetProps[`Attendee ${labelN} — Pricing`] = reason;
        }

        if (attendeeGloveOn(idx)) {
          const mod = elements.gloveModules.querySelector(`[data-glove-module][data-attendee-index="${idx}"]`);
          if (mod) {
            const product = getGloveProduct(getModuleProductId(mod));
            const hand = getGloveOptionValue(mod, 'glove_hand');
            const size = getGloveOptionValue(mod, 'glove_size');
            if (product) {
              targetProps[`Attendee ${labelN} — Glove`] = product.title;
              targetProps[`Attendee ${labelN} — Glove Hand`] = hand;
              targetProps[`Attendee ${labelN} — Glove Size`] = size;
              const variant = findVariant(product, hand, size);
              if (variant) {
                gloveLineItems.push({
                  id: variant.id, quantity: 1,
                  properties: { 'Attendee Name': name, 'Hand': hand, 'Size': size, '_attendee_index': String(n) }
                });
              }
            }
            const vipOn = mod.querySelector('[data-field-key="add_vip"]')?.checked;
            if (vipOn && vipEnabled) { vipCount += 1; targetProps[`Attendee ${labelN} — VIP`] = 'Yes'; }
          }
        }
      });

      if (freeAttendeeCount > 0 && config.freeTicketVariantId) {
        freeAttendeeProps['_event_pricing'] = `Free attendee ticket (under ${freeTicketUnderAge} and/or glove)`;
        items.push({ id: config.freeTicketVariantId, quantity: freeAttendeeCount, properties: freeAttendeeProps });
      }
      if (paidAttendeeCount > 0 && config.ticketVariantId) {
        items.push({ id: config.ticketVariantId, quantity: paidAttendeeCount, properties: paidAttendeeProps });
      }

      gloveLineItems.forEach((li) => items.push(li));
      if (vipCount > 0 && config.vipVariantId) items.push({ id: config.vipVariantId, quantity: vipCount });

      if (guestEnabled && elements.guestsContainer) {
        const guests = [];
        elements.guestsContainer.querySelectorAll('.fcd-event__guest-block').forEach((block) => {
          if (!shouldIncludeGuestBlock(block)) return;
          const get = (key) => block.querySelector(`[data-field-key="${key}"]`)?.value?.trim() || '';
          guests.push({
            name: get('guest_name'),
            email: get('guest_email'),
            phone: get('guest_phone')
          });
        });

        const freeSlots = freeGuestEnabled ? Math.min(guests.length, freeGuestAllowance()) : 0;
        const freeGuests = guests.slice(0, freeSlots);
        const paidGuests = guests.slice(freeSlots);

        if (freeGuests.length > 0 && config.freeGuestVariantId) {
          const guestProperties = { '_event_pricing': 'Glove purchase — free guest ticket' };
          freeGuests.forEach((g, i) => {
            const n = i + 1;
            guestProperties[`Guest ${n} — Name`] = g.name;
            guestProperties[`Guest ${n} — Email`] = g.email;
            guestProperties[`Guest ${n} — Phone`] = g.phone;
          });
          items.push({ id: config.freeGuestVariantId, quantity: freeGuests.length, properties: guestProperties });
        }

        if (paidGuests.length > 0 && config.guestVariantId) {
          const guestProperties = {};
          paidGuests.forEach((g, i) => {
            const n = i + 1;
            guestProperties[`Guest ${n} — Name`] = g.name;
            guestProperties[`Guest ${n} — Email`] = g.email;
            guestProperties[`Guest ${n} — Phone`] = g.phone;
          });
          items.push({ id: config.guestVariantId, quantity: paidGuests.length, properties: guestProperties });
        }
      }

      return items;
    }

    async function addItemsToCart(items) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      if (!response.ok) {
        let errorMsg = 'Cart error (' + response.status + ')';
        try { const e = await response.json(); if (e.description) errorMsg = e.description; } catch (_) {}
        throw new Error(errorMsg);
      }
      return response.json();
    }

    function setLoading(isLoading) {
      elements.submitBtn.disabled = isLoading;
      elements.submitLabel.hidden = isLoading;
      elements.submitLoading.hidden = !isLoading;
    }
    function showError(msg) { elements.errorEl.textContent = msg; elements.errorEl.hidden = false; }
    function hideError() { elements.errorEl.hidden = true; elements.errorEl.textContent = ''; }

    elements.submitBtn.addEventListener('click', (e) => { e.preventDefault(); handleSubmit(); });

    // ------ Init ------
    renderAttendees();
    renderGuests();
    renderGloveModules();
    updateSummary();
  }
})();